import MessageIcon from "@dana/Icons/MessageIcon";
import { themeColors } from "@dana/MobiusOverrides/styles/colors";
import { emailCustomerScorecard } from "@dana/Services/DanaMenuService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useRef, useState } from "react";
import { css } from "styled-components";

// --- Props Interface ---
interface CustomerScoreCardProps {
    url?: string;
    requestId?: string;
}

// --- Styles Interfaces and Objects ---
export interface CustomerScoreCardStyles {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
    messageIcon?: IconPresentationProps;
}

export const CustomerScoreCardStyles: CustomerScoreCardStyles = {
    wrapper: {
        css: css`
            .modal-actions {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 16px;
                padding: 16px 0;
            }
        `,
    },
    messageIcon: {
        color: themeColors.primary.danaBlue,
        css: css`
            &:hover {
                svg {
                    path {
                        fill: ${themeColors.secondary.danaDarkBlue};
                    }
                }
            }
        `,
    },
};

const styles = CustomerScoreCardStyles;

// --- Component Definition ---
const CustomerScoreCard = ({ requestId, url }: CustomerScoreCardProps) => {
    const modalContentRef = useRef<HTMLDivElement>(null);
    const toasterContext = React.useContext(ToasterContext);
    const [email, setEmail] = useState("");
    const [recipients, setRecipients] = React.useState<string[]>([]);
    const isValidEmail = /\S+@\S+\.\S+/.test(email);
    const [emailModalIsOpen, setEmailModalIsOpen] = useState(false);

    const modalCloseHandler = () => {
        setEmailModalIsOpen(false);
    };

    const handleAddEmail = () => {
        if (isValidEmail && !recipients.includes(email)) {
            setRecipients((prev) => [...prev, email]);
            setEmail("");
        } else if (!isValidEmail) {
            // eslint-disable-next-line no-alert
            alert("Please enter a valid email address.");
        } else {
            // eslint-disable-next-line no-alert
            alert("Email already added.");
        }
    };

    const handleSendEmail = (e: any) => {
        let emailAddress = '';
        if (recipients.length === 0) {
            if (email !== '') {
                emailAddress = email;
            } else {
                // eslint-disable-next-line no-alert
                alert("Please add at least one recipient.");
                return;
            }
        } else {
            emailAddress = recipients.join(", ");
        }
        emailCustomerScorecard(requestId ?? '', emailAddress)
            .then((response) => {
                if (response) {
                    setEmailModalIsOpen(false);
                    setRecipients([]);
                    toasterContext.addToast({ body: siteMessage("TellAFriend_Success"), messageType: "success" });
                } else {
                    toasterContext.addToast({
                        body: translate("Error Occured, Please try after some time"),
                        messageType: "danger",
                    });
                    setEmailModalIsOpen(false);
                }
            });
    };

    return (
        <StyledWrapper ref={modalContentRef} {...styles.wrapper}>
            <StyledWrapper className="modal-actions text-right">
                <Link onClick={(event) =>
                    setEmailModalIsOpen(true)
                }>
                    <IconMemo size={28} src={MessageIcon} {...styles.messageIcon} />
                </Link>
            </StyledWrapper>
            {url && <embed type="application/pdf" width="100%" height="600px" src={url}></embed>}
            <Modal headline={translate("Share")} isOpen={emailModalIsOpen} handleClose={modalCloseHandler} >

                <div style={{ display: "flex", alignItems: "center" }}>
                    <TextField type="email" label={translate("Recipients")}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        maxLength={256}
                        required
                        style={{
                            flex: 1,
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                        }}
                    />
                    <Button
                        onClick={handleAddEmail}
                        style={{
                            marginLeft: 8,
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: "#2f88ef",
                            color: "#fff",
                            fontSize: 20,
                            border: "none",
                            cursor: "pointer",
                        }}
                    >
                        +
                    </Button>
                </div>
                <Button
                    onClick={(event) =>
                        handleSendEmail(event)
                    }
                    style={{
                        marginTop: 10,
                        backgroundColor: "#2f88ef",
                        color: "#fff",
                        padding: "8px 16px",
                        border: "none",
                        borderRadius: 4,
                        fontWeight: "bold",
                        cursor: "pointer",
                    }}
                >
                    {translate("SEND EMAIL")}
                </Button>
                {recipients.length > 0 && (
                    <div style={{ marginTop: 15 }}>
                        <strong>Added Recipients:</strong>
                        <ul style={{ marginTop: 5 }}>
                            {recipients.map((r, i) => (
                                <li key={i}>{r}</li>
                            ))}
                        </ul>
                    </div>
                )}
            </Modal>
        </StyledWrapper>
    );
};
export default CustomerScoreCard;